/**
 * API Client Configuration
 *
 * Configures Axios with:
 * - Base URL from environment
 * - Authorization header injection
 * - 401 response interception with token refresh
 * - Automatic logout on refresh failure
 *
 * @module services/api
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import applyCaseMiddleware from 'axios-case-converter';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

/**
 * API error response structure from backend
 */
interface ApiErrorResponse {
  success: false;
  error?: {
    code: string;
    message: string;
  };
  message?: string;
}

/**
 * Token refresh response from backend
 * Fields are camelCase because axios-case-converter transforms them automatically.
 */
interface TokenRefreshResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

/**
 * Extended AxiosRequestConfig with retry flag
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * API base URL from environment variable
 * Falls back to localhost for development
 */
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

/**
 * Raw Axios instance (before case conversion middleware)
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout for Nigerian network conditions
  // Send cross-origin cookies (sb_device HttpOnly cookie powers the trust-aware
  // /auth/login path). The API allows credentials on its CORS config.
  withCredentials: true,
});

/**
 * Axios instance with automatic snake_case <-> camelCase conversion.
 * Request bodies are converted to snake_case, response bodies to camelCase.
 */
export const api: AxiosInstance = applyCaseMiddleware(axiosInstance);

/**
 * Flag to prevent multiple refresh attempts
 */
let isRefreshing = false;

/**
 * Queue of failed requests to retry after token refresh
 */
let failedRequestsQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
}> = [];

/**
 * Process queued requests after token refresh
 */
function processQueue(error: AxiosError | null, token: string | null): void {
  failedRequestsQueue.forEach((request) => {
    if (error !== null) {
      request.reject(error);
    } else if (token !== null) {
      request.resolve(token);
    }
  });
  failedRequestsQueue = [];
}

/**
 * Request interceptor - adds Authorization header
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const { tokens } = useAuthStore.getState();

    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    // Attach unique request ID for backend correlation (with Safari fallback for older Nigerian devices)
    config.headers['X-Request-ID'] =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString()}-${Math.random().toString(36).slice(2, 11)}`;

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * Response interceptor - handles 401 errors with token refresh
 *
 * Flow:
 * 1. On 401 response, attempt to refresh the token
 * 2. If refresh succeeds, retry the original request
 * 3. If refresh fails, logout and redirect to login
 * 4. Queue concurrent requests during refresh to avoid multiple refresh calls
 */
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 429 rate limiting with interceptor-level toast (transient, affects all requests)
    if (error.response?.status === 429) {
      // Fixed id: TanStack retries every failed attempt through this interceptor, and several
      // queries can hit the limit at once; one visible toast, not one per attempt.
      useUIStore.getState().showToast({
        id: 'rate-limited',
        message: 'Too many requests. Please wait a moment and try again.',
        type: 'warning',
        duration: 5000,
      });
      return Promise.reject(error);
    }

    // Only handle 401 errors and avoid retrying the same request
    if (error.response?.status !== 401 || originalRequest._retry === true) {
      return Promise.reject(error);
    }

    // Don't retry auth endpoints (they have their own error handling)
    if (originalRequest.url?.includes('/auth/')) {
      return Promise.reject(error);
    }

    const { tokens, logout, setTokens } = useAuthStore.getState();

    // No refresh token available - logout immediately
    if (tokens?.refreshToken === undefined || tokens.refreshToken === null) {
      logout();
      redirectToLogin();
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise<AxiosResponse>((resolve, reject) => {
        failedRequestsQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject: (err: AxiosError) => {
            reject(err);
          },
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Call refresh endpoint (use the case-converting api instance)
      const response = await api.post<TokenRefreshResponse>(
        '/auth/refresh',
        { refreshToken: tokens.refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { accessToken: newAccessToken, refreshToken: newRefreshToken, expiresIn } = response.data;

      // Calculate expiration timestamp
      const expiresAt = Date.now() + expiresIn * 1000;

      // Update tokens in store
      setTokens({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        expiresAt,
      });

      // Process queued requests
      processQueue(null, newAccessToken);

      // Retry original request with new token
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      // Refresh failed - logout and clear queue
      processQueue(refreshError as AxiosError, null);
      logout();
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * Redirect to login page
 *
 * Uses a slight delay to allow state updates to propagate
 */
function redirectToLogin(): void {
  // Small delay to ensure store is updated before redirect
  setTimeout(() => {
    // Only redirect if not already on auth pages
    if (!window.location.pathname.startsWith('/auth')) {
      window.location.href = '/auth/login';
    }
  }, 100);
}

/**
 * Type-safe GET request helper
 */
export async function apiGet<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.get<T>(url, config);
  return response.data;
}

/**
 * Type-safe POST request helper
 */
export async function apiPost<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.post<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe PUT request helper
 */
export async function apiPut<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.put<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe PATCH request helper
 */
export async function apiPatch<T, D = unknown>(
  url: string,
  data?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.patch<T>(url, data, config);
  return response.data;
}

/**
 * Type-safe DELETE request helper
 */
export async function apiDelete<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await api.delete<T>(url, config);
  return response.data;
}
